import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { speak } from '../utils/speak';

const API = '/api';

function Translate() {
  const [word, setWord] = useState('');
  const [sourceLang, setSourceLang] = useState('DE');
  const [targetLang, setTargetLang] = useState('EN');
  const [translation, setTranslation] = useState(null);
  const [languages, setLanguages] = useState({});
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/translate/languages`).then(res => setLanguages(res.data));
    axios.get(`${API}/categories`).then(res => setCategories(res.data.flat));
  }, []);

  const handleTranslate = async () => {
    if (!word.trim()) return;
    setLoading(true);
    setTranslation(null);
    setNotes('');
    try {
      const res = await axios.post(`${API}/translate`, { word, sourceLang, targetLang });
      setTranslation(res.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Übersetzung fehlgeschlagen' });
    }
    setLoading(false);
  };

const handleSave = async () => {
  if (!translation) return;
  try {
    await axios.post(`${API}/cards`, {
      word: translation.word,
      translation: translation.translation,
      sourceLang: translation.sourceLang,
      targetLang: translation.targetLang,
      categoryId: selectedCategory || null,
      exampleSource: translation.exampleSource || null,
      exampleTarget: translation.exampleTarget || null,
      notes: notes || null,
    });
    setMessage({ type: 'success', text: 'Karte gespeichert!' });
    setNotes('');
    setTimeout(() => setMessage(null), 3000);
  } catch (err) {
    if (err.response?.data?.upgrade) {
      setMessage({
        type: 'error',
        text: err.response.data.message
      });
    } else {
      setMessage({ type: 'error', text: 'Speichern fehlgeschlagen' });
    }
  }
};

  return (
    <div>
      <h1>Wort übersetzen</h1>

      <div className="card">
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
          <select value={sourceLang} onChange={e => setSourceLang(e.target.value)} style={{ flex: 1 }}>
            {Object.entries(languages).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
          <button className="btn-secondary" onClick={() => {
            const tmp = sourceLang;
            setSourceLang(targetLang);
            setTargetLang(tmp);
            setTranslation(null);
          }}>⇄</button>
          <select value={targetLang} onChange={e => setTargetLang(e.target.value)} style={{ flex: 1 }}>
            {Object.entries(languages).map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </div>

        <input
          type="text"
          placeholder="Wort oder Satz eingeben..."
          value={word}
          onChange={e => setWord(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleTranslate()}
        />

        <button className="btn-primary" onClick={handleTranslate} disabled={loading}>
          {loading ? 'Übersetze...' : 'Übersetzen'}
        </button>
      </div>

      {translation && (
        <div className="card">
          {/* Originalwort */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.25rem' }}>
              {translation.sourceLang}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '600' }}>{translation.word}</div>
              <button onClick={() => speak(translation.word, translation.sourceLang)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>
                Anhören
              </button>
            </div>
          </div>

          {/* Übersetzung */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.25rem' }}>
              {translation.targetLang}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: '600', color: '#4c51bf' }}>
                {translation.translation}
              </div>
              <button onClick={() => speak(translation.translation, translation.targetLang)}
                style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer' }}>
                Anhören
              </button>
            </div>
          </div>

          {/* Beispielsatz */}
          {translation.exampleSource && (
            <div style={{
              background: '#f7fafc', borderRadius: '8px',
              padding: '1rem', marginBottom: '1.5rem',
              borderLeft: '4px solid #4c51bf'
            }}>
              <div style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '0.5rem', fontWeight: '600' }}>
                Beispielsatz
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.95rem' }}>{translation.exampleSource}</span>
                <button onClick={() => speak(translation.exampleSource, translation.sourceLang)}
                  style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer' }}>
                  Anhören
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.95rem', color: '#4c51bf' }}>{translation.exampleTarget}</span>
                <button onClick={() => speak(translation.exampleTarget, translation.targetLang)}
                  style={{ background: 'none', border: 'none', fontSize: '1rem', cursor: 'pointer' }}>
                  Anhören
                </button>
              </div>
            </div>
          )}

          {/* Kategorie */}
          <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
            <option value="">Keine Kategorie</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          {/* Notizen */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontSize: '0.85rem', color: '#718096', display: 'block', marginBottom: '0.25rem' }}>
              Notizen (optional)
            </label>
            <textarea
              placeholder="Eselsbrücke, Kontext, eigene Notizen..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{
                width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0',
                borderRadius: '8px', fontSize: '0.95rem', resize: 'vertical',
                minHeight: '80px', fontFamily: 'inherit'
              }}
            />
          </div>

          {message && <div className={`message message-${message.type}`}>{message.text}</div>}

          <button className="btn-success" onClick={handleSave}>
            Als Lernkarte speichern
          </button>
        </div>
      )}
    </div>
  );
}

export default Translate;